import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CpuIcon, ShieldCheckIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import LinkedSection from "../../components/crm/LinkedSection";
import InlineEditableField from "../../components/crm/InlineEditableField";
import {
  ConfirmDelete,
  DateInput,
  ErrorBanner,
  Field,
  Modal,
  NumberInput,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../components/crm/Form";
import { FieldCard } from "./CustomerDetail";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";
import SubscriptionStatusBadge from "../../components/billing/SubscriptionStatusBadge";

const WRITE_ROLES = ["owner", "ops"];
const FINANCING_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "loan", label: "Loan" },
  { value: "lease", label: "Lease" },
];
const EQUIPMENT_MFRS = [
  { value: "qcells", label: "QCells" },
  { value: "silfab", label: "Silfab" },
  { value: "mission", label: "Mission" },
  { value: "rec", label: "REC" },
  { value: "others", label: "Others" },
];
const COVERAGE_TYPES = [
  { value: "labor", label: "Labor" },
  { value: "parts", label: "Parts" },
  { value: "full", label: "Full" },
];
const PLATFORM_TYPES = [
  { value: "enphase", label: "Enphase" },
  { value: "solaredge", label: "SolarEdge" },
  { value: "tesla", label: "Tesla" },
  { value: "eg4", label: "EG4" },
  { value: "generic", label: "Generic" },
];

export default function SolarSystemDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [doc, setDoc] = useState(null);
  const [subs, setSubs] = useState([]);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [eqOpen, setEqOpen] = useState(false);
  const [warOpen, setWarOpen] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/solar-systems/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
    try {
      const { data } = await api.get("/subscriptions/", {
        params: { solar_system_id: id },
      });
      setSubs(data || []);
    } catch {
      setSubs([]);
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/solar-systems/${id}`);
      nav("/solar-systems");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Solar System">
        <ErrorBanner message={err} />
      </Layout>
    );

  return (
    <Layout title={`Solar System · ${doc.install_year}`} subtitle="Install record">
      <EntityHeader
        overline="solar system"
        title={`${doc.install_year} install`}
        breadcrumb={[
          { label: "Solar Systems", to: "/solar-systems" },
          { label: `${doc.install_year} install` },
        ]}
        meta={
          <>
            <span>property · {doc.property_id.slice(0, 8)}</span>
            <span>financing · {doc.financing_type}</span>
            {doc.warranty_expires && (
              <span>system warranty · {doc.warranty_expires}</span>
            )}
          </>
        }
        canEdit={canWrite}
        canDelete={canWrite}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <FieldCard label="Install Year" value={String(doc.install_year)} mono />
        <InlineEditableField
          label="Original Installer"
          value={doc.original_installer}
          name="original_installer"
          canEdit={canWrite}
          endpoint={`/solar-systems/${id}`}
          placeholder="Add installer"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="system-inline"
        />
        <InlineEditableField
          label="Financing"
          value={doc.financing_type}
          name="financing_type"
          mono
          as="select"
          options={FINANCING_OPTIONS}
          canEdit={canWrite}
          endpoint={`/solar-systems/${id}`}
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="system-inline"
        />
        <InlineEditableField
          label="Utility Company"
          value={doc.utility_company}
          name="utility_company"
          canEdit={canWrite}
          endpoint={`/solar-systems/${id}`}
          placeholder="Add utility"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="system-inline"
        />
        <InlineEditableField
          label="System Warranty"
          value={doc.warranty_expires}
          name="warranty_expires"
          mono
          as="date"
          canEdit={canWrite}
          endpoint={`/solar-systems/${id}`}
          placeholder="Set expiry"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="system-inline"
        />
        <FieldCard label="Equipment Count" value={String(doc.equipment.length)} mono />
        <InlineEditableField
          label="Notes"
          value={doc.notes}
          name="notes"
          as="textarea"
          canEdit={canWrite}
          endpoint={`/solar-systems/${id}`}
          placeholder="Add notes"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="system-inline"
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      <LinkedSection
        title="Equipment"
        subtitle="hardware on this system"
        count={doc.equipment.length}
        canCreate={canWrite}
        onCreate={() => setEqOpen(true)}
        addLabel="Add Equipment"
        emptyTitle="No equipment yet"
        emptyDescription="Add panels, inverters, batteries, or other hardware tied to this system."
        data-testid="system-equipment-section"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Manufacturer</th>
              <th className="text-left px-5 py-3 font-bold">Model</th>
              <th className="text-left px-5 py-3 font-bold">Serial</th>
              <th className="text-left px-5 py-3 font-bold">Installed</th>
              <th className="text-left px-5 py-3 font-bold">Warranty</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {doc.equipment.map((e) => (
              <tr
                key={e.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/equipment/${e.id}`)}
                data-testid={`equipment-row-${e.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <CpuIcon
                    size={14}
                    weight="duotone"
                    className="text-slate-400"
                  />
                  {e.manufacturer}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">{e.model}</td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {e.serial_number}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {e.install_date || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {e.warranty_expires || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LinkedSection>

      <div className="mt-6">
        <LinkedSection
          title="Warranties"
          subtitle="all warranty records across this system"
          count={doc.warranties.length}
          canCreate={canWrite && doc.equipment.length > 0}
          onCreate={() => setWarOpen(true)}
          addLabel="Add Warranty"
          emptyTitle="No warranty records"
          emptyDescription={
            doc.equipment.length === 0
              ? "Add equipment first, then attach warranties to specific items."
              : "Capture manufacturer warranty terms tied to specific equipment."
          }
          data-testid="system-warranties-section"
        >
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
                <th className="text-left px-5 py-3 font-bold">Manufacturer</th>
                <th className="text-left px-5 py-3 font-bold">Platform</th>
                <th className="text-left px-5 py-3 font-bold">Coverage</th>
                <th className="text-left px-5 py-3 font-bold">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {doc.warranties.map((w) => (
                <tr
                  key={w.id}
                  className="cursor-pointer hover:bg-slate-50/60"
                  onClick={() => nav(`/warranties/${w.id}`)}
                  data-testid={`warranty-row-${w.id}`}
                >
                  <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                    <ShieldCheckIcon
                      size={14}
                      weight="duotone"
                      className="text-emerald-600"
                    />
                    {w.manufacturer}
                  </td>
                  <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                    {w.platform_type || "—"}
                  </td>
                  <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                    {w.coverage_type}
                  </td>
                  <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                    {w.expires}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </LinkedSection>
      </div>

      {/* Phase 6 — Subscriptions linked to this system */}
      <LinkedSection
        title="Protection Plan Subscriptions"
        subtitle="recurring plans covering this system"
        count={subs.length}
        emptyTitle="No plans cover this system"
        emptyDescription="Enroll the customer in a protection plan from their detail page."
        data-testid="system-subscriptions-section"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Plan</th>
              <th className="text-left px-5 py-3 font-bold">Status</th>
              <th className="text-left px-5 py-3 font-bold">Next renewal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {subs.map((s) => (
              <tr
                key={s.id}
                className="hover:bg-slate-50/60 cursor-pointer"
                onClick={() => nav(`/subscriptions/${s.id}`)}
                data-testid={`system-sub-row-${s.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <ShieldCheckIcon
                    size={14}
                    weight="duotone"
                    className="text-amber-600"
                  />
                  {s.plan_name}
                </td>
                <td className="px-5 py-3">
                  <SubscriptionStatusBadge status={s.status} />
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700">
                  {s.current_period_end || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LinkedSection>

      {canWrite && (
        <>
          <EditSystemModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            system={doc}
            onSaved={(d) => {
              setEditOpen(false);
              setDoc({ ...doc, ...d });
            }}
          />
          <ConfirmDelete
            open={delOpen}
            onClose={() => setDelOpen(false)}
            onConfirm={handleDelete}
            label="Solar System"
            busy={delBusy}
          />
          <CreateEquipmentModal
            open={eqOpen}
            onClose={() => setEqOpen(false)}
            systemId={doc.id}
            onCreated={() => {
              setEqOpen(false);
              load();
            }}
          />
          <CreateWarrantyModal
            open={warOpen}
            onClose={() => setWarOpen(false)}
            equipmentOptions={doc.equipment.map((e) => ({
              value: e.id,
              label: `${e.manufacturer} · ${e.model} · ${e.serial_number}`,
            }))}
            onCreated={() => {
              setWarOpen(false);
              load();
            }}
          />
        </>
      )}
    </Layout>
  );
}

function EditSystemModal({ open, onClose, system, onSaved }) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        install_year: system.install_year,
        original_installer: system.original_installer || "",
        financing_type: system.financing_type,
        utility_company: system.utility_company || "",
        warranty_expires: system.warranty_expires || "",
        notes: system.notes || "",
      });
      setErr("");
    }
  }, [open, system]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.patch(`/solar-systems/${system.id}`, {
        install_year: Number(form.install_year),
        original_installer: form.original_installer || null,
        financing_type: form.financing_type,
        utility_company: form.utility_company || null,
        warranty_expires: form.warranty_expires || null,
        notes: form.notes || null,
      });
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Solar System"
      subtitle="update record"
      data-testid="system-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Install Year" required>
            <NumberInput
              required
              min={1990}
              max={2100}
              value={form.install_year}
              onChange={(v) => setForm({ ...form, install_year: v })}
              data-testid="system-edit-year"
            />
          </Field>
          <Field label="Financing" required>
            <Select
              value={form.financing_type}
              onChange={(v) =>
                setForm({ ...form, financing_type: v || "cash" })
              }
              options={FINANCING_OPTIONS}
              placeholder=""
              data-testid="system-edit-financing"
            />
          </Field>
        </div>
        <Field label="Original Installer">
          <TextInput
            value={form.original_installer}
            onChange={(v) => setForm({ ...form, original_installer: v })}
          />
        </Field>
        <Field label="Utility Company">
          <TextInput
            value={form.utility_company}
            onChange={(v) => setForm({ ...form, utility_company: v })}
          />
        </Field>
        <Field label="System Warranty Expires">
          <DateInput
            value={form.warranty_expires}
            onChange={(v) => setForm({ ...form, warranty_expires: v })}
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="system-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function CreateEquipmentModal({ open, onClose, systemId, onCreated }) {
  const [form, setForm] = useState({
    manufacturer: "qcells",
    model: "",
    serial_number: "",
    install_date: "",
    warranty_expires: "",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        manufacturer: "qcells",
        model: "",
        serial_number: "",
        install_date: "",
        warranty_expires: "",
        notes: "",
      });
      setErr("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        solar_system_id: systemId,
        manufacturer: form.manufacturer,
        model: form.model,
        serial_number: form.serial_number,
      };
      if (form.install_date) payload.install_date = form.install_date;
      if (form.warranty_expires)
        payload.warranty_expires = form.warranty_expires;
      if (form.notes) payload.notes = form.notes;
      const { data } = await api.post("/equipment/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Equipment"
      subtitle="hardware record"
      data-testid="equipment-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Manufacturer" required>
          <Select
            value={form.manufacturer}
            onChange={(v) => setForm({ ...form, manufacturer: v || "qcells" })}
            options={EQUIPMENT_MFRS}
            placeholder=""
            data-testid="equipment-mfr"
          />
        </Field>
        <Field label="Model" required>
          <TextInput
            required
            value={form.model}
            onChange={(v) => setForm({ ...form, model: v })}
            data-testid="equipment-model"
          />
        </Field>
        <Field label="Serial Number" required>
          <TextInput
            required
            value={form.serial_number}
            onChange={(v) => setForm({ ...form, serial_number: v })}
            data-testid="equipment-serial"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Install Date">
            <DateInput
              value={form.install_date}
              onChange={(v) => setForm({ ...form, install_date: v })}
              data-testid="equipment-install-date"
            />
          </Field>
          <Field label="Warranty Expires">
            <DateInput
              value={form.warranty_expires}
              onChange={(v) => setForm({ ...form, warranty_expires: v })}
              data-testid="equipment-warranty-expires"
            />
          </Field>
        </div>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="equipment-submit"
          >
            {busy ? "Adding…" : "Add Equipment"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function CreateWarrantyModal({ open, onClose, equipmentOptions, onCreated }) {
  const [form, setForm] = useState({
    equipment_id: equipmentOptions[0]?.value || "",
    manufacturer: "",
    platform_type: "",
    coverage_type: "full",
    expires: "",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        equipment_id: equipmentOptions[0]?.value || "",
        manufacturer: "",
        platform_type: "",
        coverage_type: "full",
        expires: "",
        notes: "",
      });
      setErr("");
    }
  }, [open, equipmentOptions]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        equipment_id: form.equipment_id,
        manufacturer: form.manufacturer,
        coverage_type: form.coverage_type,
        expires: form.expires,
      };
      if (form.platform_type) payload.platform_type = form.platform_type;
      if (form.notes) payload.notes = form.notes;
      const { data } = await api.post("/warranties/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Warranty"
      subtitle="warranty record"
      data-testid="warranty-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Equipment" required>
          <Select
            value={form.equipment_id}
            onChange={(v) => setForm({ ...form, equipment_id: v || "" })}
            options={equipmentOptions}
            placeholder=""
            data-testid="warranty-equipment"
          />
        </Field>
        <Field label="Manufacturer" required hint="Freeform — e.g. Enphase, QCells, ABC Roofing.">
          <TextInput
            required
            value={form.manufacturer}
            onChange={(v) => setForm({ ...form, manufacturer: v })}
            data-testid="warranty-mfr"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Platform Type">
            <Select
              value={form.platform_type}
              onChange={(v) => setForm({ ...form, platform_type: v || "" })}
              options={PLATFORM_TYPES}
              placeholder="None"
              data-testid="warranty-platform"
            />
          </Field>
          <Field label="Coverage" required>
            <Select
              value={form.coverage_type}
              onChange={(v) =>
                setForm({ ...form, coverage_type: v || "full" })
              }
              options={COVERAGE_TYPES}
              placeholder=""
              data-testid="warranty-coverage"
            />
          </Field>
        </div>
        <Field label="Expires" required>
          <DateInput
            required
            value={form.expires}
            onChange={(v) => setForm({ ...form, expires: v })}
            data-testid="warranty-expires"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="warranty-submit"
          >
            {busy ? "Adding…" : "Add Warranty"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
